import {
	useCallback,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type RefObject,
} from 'react';
import { cn } from '@/lib/utils';
import { PRONTUARIO_NOTES_INPUT_CLASS } from '../../constants/prontuarioTheme';
import {
	applyProntuarioNotesDisplayChange,
	buildProntuarioNotesDisplayModel,
	displaySelectionNeedsRefresh,
	displayOffsetToSource,
	sourceOffsetToDisplay,
	type ProntuarioNotesCursor,
} from '../../utils/buildProntuarioNotesDisplayModel';
import { normalizeProntuarioNotesInput } from '../../utils/normalizeProntuarioNotesInput';
import { renderProntuarioNotesPreview } from '../../utils/renderProntuarioNotesPreview';
import { BULLET_PREFIX } from '../../utils/parseProntuarioNotesSegments';

type SessionCallProntuarioNotesEditorProps = {
	id: string;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
	ariaLabel?: string;
	textareaRef?: RefObject<HTMLTextAreaElement>;
	className?: string;
};

const EDITOR_TEXT_CLASS =
	'text-[15px] leading-[1.65] font-normal tracking-normal text-slate-200';

const SessionCallProntuarioNotesEditor = ({
	id,
	value,
	onChange,
	placeholder = 'Sua anotação...',
	ariaLabel,
	textareaRef,
	className,
}: SessionCallProntuarioNotesEditorProps) => {
	const internalRef = useRef<HTMLTextAreaElement>(null);
	const resolvedRef = textareaRef ?? internalRef;
	const previewRef = useRef<HTMLDivElement>(null);
	const displaySelectionRef = useRef<ProntuarioNotesCursor>({
		start: 0,
		end: 0,
	});
	const [sourceCursor, setSourceCursor] = useState<ProntuarioNotesCursor>({
		start: 0,
		end: 0,
	});

	const model = useMemo(
		() => buildProntuarioNotesDisplayModel(value, sourceCursor),
		[value, sourceCursor],
	);

	const lastSyncedDisplayRef = useRef<ProntuarioNotesCursor>({
		start: 0,
		end: 0,
	});
	const lastDisplayValueRef = useRef('');

	const syncDisplaySelection = useCallback(
		(cursor: ProntuarioNotesCursor, force = false) => {
			const textarea = resolvedRef.current;
			if (!textarea) return;

			const displayStart = sourceOffsetToDisplay(model, cursor.start);
			const displayEnd = sourceOffsetToDisplay(model, cursor.end);

			if (
				!force &&
				textarea.selectionStart === displayStart &&
				textarea.selectionEnd === displayEnd
			) {
				displaySelectionRef.current = {
					start: displayStart,
					end: displayEnd,
				};
				lastSyncedDisplayRef.current = {
					start: displayStart,
					end: displayEnd,
				};
				return;
			}

			textarea.selectionStart = displayStart;
			textarea.selectionEnd = displayEnd;
			displaySelectionRef.current = {
				start: displayStart,
				end: displayEnd,
			};
			lastSyncedDisplayRef.current = {
				start: displayStart,
				end: displayEnd,
			};
		},
		[model, resolvedRef],
	);

	useLayoutEffect(() => {
		const displayChanged = lastDisplayValueRef.current !== model.display;
		lastDisplayValueRef.current = model.display;
		syncDisplaySelection(sourceCursor, displayChanged);
	}, [model.display, sourceCursor, syncDisplaySelection]);

	const syncScroll = () => {
		const textarea = resolvedRef.current;
		const preview = previewRef.current;
		if (!textarea || !preview) return;
		preview.scrollTop = textarea.scrollTop;
		preview.scrollLeft = textarea.scrollLeft;
	};

	const updateSourceCursorFromDisplay = (
		displaySelection: ProntuarioNotesCursor,
	) => {
		const nextSourceCursor = {
			start: displayOffsetToSource(model, displaySelection.start),
			end: displayOffsetToSource(model, displaySelection.end),
		};

		displaySelectionRef.current = displaySelection;

		if (
			displaySelectionNeedsRefresh(value, sourceCursor, nextSourceCursor)
		) {
			setSourceCursor(nextSourceCursor);
			return;
		}

		if (
			nextSourceCursor.start !== sourceCursor.start ||
			nextSourceCursor.end !== sourceCursor.end
		) {
			setSourceCursor(nextSourceCursor);
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		const nextDisplay = event.target.value;
		const { source, cursor } = applyProntuarioNotesDisplayChange(
			value,
			model,
			nextDisplay,
		);

		setSourceCursor(cursor);
		onChange(normalizeProntuarioNotesInput(source));
	};

	const handleSelectionUpdate = (textarea: HTMLTextAreaElement) => {
		const displaySelection = {
			start: textarea.selectionStart,
			end: textarea.selectionEnd,
		};

		if (
			displaySelection.start === lastSyncedDisplayRef.current.start &&
			displaySelection.end === lastSyncedDisplayRef.current.end
		) {
			return;
		}

		updateSourceCursorFromDisplay(displaySelection);
	};

	const handleSelect = (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
		handleSelectionUpdate(event.currentTarget);
	};

	const ARROW_KEYS = new Set([
		'ArrowLeft',
		'ArrowRight',
		'ArrowUp',
		'ArrowDown',
		'Home',
		'End',
	]);

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (ARROW_KEYS.has(event.key)) {
			requestAnimationFrame(() => {
				const textarea = resolvedRef.current;
				if (!textarea) return;
				handleSelectionUpdate(textarea);
			});
			return;
		}

		if (event.key !== 'Enter') return;

		const textarea = event.currentTarget;
		const { selectionStart, selectionEnd } = textarea;
		if (selectionStart !== selectionEnd) return;

		const sourcePos = displayOffsetToSource(model, selectionStart);
		const lineStart = value.lastIndexOf('\n', sourcePos - 1) + 1;
		const currentLine = value.slice(lineStart, sourcePos);

		if (!currentLine.startsWith(BULLET_PREFIX)) return;

		event.preventDefault();
		const insert = `\n${BULLET_PREFIX}`;
		const nextValue =
			value.slice(0, sourcePos) + insert + value.slice(sourcePos);
		const nextCursor = {
			start: sourcePos + insert.length,
			end: sourcePos + insert.length,
		};

		setSourceCursor(nextCursor);
		onChange(nextValue);
	};

	return (
		<div className={cn('relative min-h-0 flex-1', className)}>
			<div
				ref={previewRef}
				aria-hidden
				className={cn(
					'daily-scrollbar pointer-events-none absolute inset-0 overflow-auto whitespace-pre-wrap break-words',
					EDITOR_TEXT_CLASS,
				)}
			>
				{model.display ? (
					renderProntuarioNotesPreview(model.chunks)
				) : (
					<span className="text-slate-600">{placeholder}</span>
				)}
			</div>

			<textarea
				id={id}
				ref={resolvedRef}
				value={model.display}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				onKeyUp={handleSelect}
				onClick={handleSelect}
				onSelect={handleSelect}
				onScroll={syncScroll}
				placeholder=""
				aria-label={ariaLabel}
				spellCheck
				className={cn(
					'daily-scrollbar absolute inset-0 z-[1] h-full w-full resize-none',
					PRONTUARIO_NOTES_INPUT_CLASS,
					EDITOR_TEXT_CLASS,
					'whitespace-pre-wrap break-words bg-transparent text-transparent caret-slate-200 selection:bg-white/20',
				)}
			/>
		</div>
	);
};

export { SessionCallProntuarioNotesEditor };
